import React, { useCallback } from "react";
import { TextField, Dropdown, IconButton } from "@fluentui/react";
import type { IDropdownOption } from "@fluentui/react";
import type { HeaderTypeValue } from "@/components/telemetry/search/useTelemetrySearch";
import styles from "@/components/telemetry/search/TelemetrySearchToolbar.module.css";

const HttpMethod = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  DELETE: "DELETE",
  HEAD: "HEAD",
  OPTIONS: "OPTIONS",
} as const;

const HeaderType = {
  Request: "request",
  Response: "response",
} as const;

interface TelemetrySearchToolbarProps {
  customerId: string;
  method: string;
  path: string;
  statusCode: string;
  apiName: string;
  fromTimestamp: string;
  toTimestamp: string;
  headerName: string;
  headerValue: string;
  headerType: HeaderTypeValue;
  onCustomerIdChange: (value: string) => void;
  onMethodChange: (value: string) => void;
  onPathChange: (value: string) => void;
  onStatusCodeChange: (value: string) => void;
  onApiNameChange: (value: string) => void;
  onFromTimestampChange: (value: string) => void;
  onToTimestampChange: (value: string) => void;
  onHeaderNameChange: (value: string) => void;
  onHeaderValueChange: (value: string) => void;
  onHeaderTypeChange: (value: HeaderTypeValue) => void;
  onRefresh: () => void;
}

const METHOD_OPTIONS: IDropdownOption[] = [
  { key: "", text: "All" },
  ...Object.values(HttpMethod).map((m) => ({ key: m, text: m })),
];

const HEADER_TYPE_OPTIONS: IDropdownOption[] = [
  { key: HeaderType.Request, text: "Request" },
  { key: HeaderType.Response, text: "Response" },
];

const labelStyle = {
  root: { fontSize: 12, fontWeight: 600 as const, color: "#605e5c", padding: 0 },
};

const textFieldStyles = {
  root: { width: "100%" },
  subComponentStyles: { label: labelStyle },
  icon: { color: "#605e5c" },
};

const dropdownStyles = {
  root: { width: "100%" },
  label: labelStyle.root,
};

const DEBOUNCE_MS = 400;

function TelemetrySearchToolbar({
  customerId,
  method,
  path,
  statusCode,
  apiName,
  fromTimestamp,
  toTimestamp,
  headerName,
  headerValue,
  headerType,
  onCustomerIdChange,
  onMethodChange,
  onPathChange,
  onStatusCodeChange,
  onApiNameChange,
  onFromTimestampChange,
  onToTimestampChange,
  onHeaderNameChange,
  onHeaderValueChange,
  onHeaderTypeChange,
  onRefresh,
}: TelemetrySearchToolbarProps) {
  const handleMethodChange = useCallback(
    (_: unknown, option?: IDropdownOption) => {
      if (option) onMethodChange(String(option.key));
    },
    [onMethodChange],
  );

  const handleHeaderTypeChange = useCallback(
    (_: unknown, option?: IDropdownOption) => {
      if (option) onHeaderTypeChange(option.key as HeaderTypeValue);
    },
    [onHeaderTypeChange],
  );

  return (
    <div className={styles.toolbar}>
      <div className={styles.grid}>
        <IconButton
          iconProps={{ iconName: "Refresh" }}
          title="Refresh"
          ariaLabel="Refresh"
          onClick={onRefresh}
          styles={{
            root: { width: 32, height: 32, color: "#0b2e51" },
            rootHovered: { color: "#0b2e51", background: "#f3f2f1" },
          }}
        />
        <TextField
          label="Customer ID"
          iconProps={{ iconName: "Contact" }}
          defaultValue={customerId}
          onChange={(_, val) => onCustomerIdChange(val ?? "")}
          deferredValidationTime={DEBOUNCE_MS}
          styles={textFieldStyles}
        />
        <Dropdown
          label="Method"
          options={METHOD_OPTIONS}
          selectedKey={method}
          onChange={handleMethodChange}
          styles={dropdownStyles}
        />
        <TextField
          label="Path"
          iconProps={{ iconName: "Globe" }}
          defaultValue={path}
          onChange={(_, val) => onPathChange(val ?? "")}
          deferredValidationTime={DEBOUNCE_MS}
          styles={textFieldStyles}
        />
        <TextField
          label="Status Code"
          iconProps={{ iconName: "NumberSymbol" }}
          defaultValue={statusCode}
          onChange={(_, val) => onStatusCodeChange(val ?? "")}
          deferredValidationTime={DEBOUNCE_MS}
          styles={textFieldStyles}
        />
        <TextField
          label="API Name"
          iconProps={{ iconName: "Code" }}
          defaultValue={apiName}
          onChange={(_, val) => onApiNameChange(val ?? "")}
          deferredValidationTime={DEBOUNCE_MS}
          styles={textFieldStyles}
        />

        <div />
        <TextField
          label="From"
          placeholder="MM/dd/yyyy"
          iconProps={{ iconName: "Calendar" }}
          defaultValue={fromTimestamp}
          onChange={(_, val) => onFromTimestampChange(val ?? "")}
          styles={textFieldStyles}
        />
        <TextField
          label="To"
          placeholder="MM/dd/yyyy"
          iconProps={{ iconName: "Calendar" }}
          defaultValue={toTimestamp}
          onChange={(_, val) => onToTimestampChange(val ?? "")}
          styles={textFieldStyles}
        />
        <Dropdown
          label="Header Type"
          options={HEADER_TYPE_OPTIONS}
          selectedKey={headerType}
          onChange={handleHeaderTypeChange}
          styles={dropdownStyles}
        />
        <TextField
          label="Header Name"
          iconProps={{ iconName: "Tag" }}
          defaultValue={headerName}
          onChange={(_, val) => onHeaderNameChange(val ?? "")}
          deferredValidationTime={DEBOUNCE_MS}
          styles={textFieldStyles}
        />
        <TextField
          label="Header Value"
          iconProps={{ iconName: "Search" }}
          defaultValue={headerValue}
          onChange={(_, val) => onHeaderValueChange(val ?? "")}
          deferredValidationTime={DEBOUNCE_MS}
          styles={textFieldStyles}
        />
      </div>
    </div>
  );
}

export default TelemetrySearchToolbar;
